// Confirmation dialog for destructive / irreversible-feeling actions
// (notifications-toast.md: "confirmations that pause the user → modal"). Built
// on the accessible <Modal> primitive. Used by the soft-delete + restore flows
// across the management features (PRD-32 §FR-2).
//
// PRD-60 chunk 0: the confirm button is a real `destructive` Button instead
// of a red className painted over `primary`; the footer is pinned under the
// scrolling body and stacks on a phone with Cancel closest to the thumb.
// Focus lands on Cancel, not on the destructive button: the safe option is
// the default for a keyboard user who hit Enter on the way in.

import { useTranslation } from "react-i18next"

import { Button } from "./Button"
import { Modal } from "./Modal"

interface Props {
  open: boolean
  title: string
  message: string
  /** Confirm-button label. Defaults to common:actions.confirm. */
  confirmLabel?: string
  /** "destructive" for anything that removes or disables (soft-delete);
   *  "default" for the reversible direction (restore). */
  tone?: "destructive" | "default"
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  tone = "destructive",
  loading,
  onConfirm,
  onClose,
}: Props) {
  const { t } = useTranslation("common")

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      className="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose} disabled={loading} data-autofocus>
            {t("actions.cancel")}
          </Button>
          <Button
            variant={tone === "destructive" ? "destructive" : "primary"}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel ?? t("actions.confirm")}
          </Button>
        </div>
      }
    >
      <p className="text-ink-muted text-sm whitespace-pre-line">{message}</p>
    </Modal>
  )
}
